import { useState } from "react";
import { getLocalId } from "easy-analytics/client";
import { api, handleError } from "../../services/api";

import { Footer, Header } from "../../Layout";

import { useRouter } from "easy-page-router/react";
import { useHead } from "../../services/common";
import { useLang } from "../../services/lang";
import { ThemeSelect, ThemeSwitch } from "../../components/Theme";
import { LangSwitch } from "../../components/Lang";
import { Checkbox, CheckboxPublic, Input, InputNumber } from "../../components/Form";
import { Loading, Page } from "../../components";

import { Poll, PollOption } from "../../../types";

const emptyPoll: Poll = {
    _id: "",
    userId: "",
    changed: "",
    lang: "en",
    theme: "",
    title: "",
    description: "",
    voteCount: 1,
    public: false,
    users: {},
    options: {},
}


export default function PollForm() {
    const { push } = useRouter();
    const { lang, dict } = useLang();

    useHead({
        title: dict.seo.pollForm.title,
        description: dict.seo.pollForm.description,
    });

    const [poll, setPoll] = useState(emptyPoll);
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);

    const [options, setOptions] = useState<string[]>([""]);

    function handleChangeOption(i: number, newValue: string) {
        const newOptions = [...options];
        newOptions[i] = newValue;

        // set last empty
        if (newOptions[newOptions.length - 1].length > 0)
            newOptions.push("");

        setOptions(newOptions);
    }

    function handleBlurOption() {
        setOptions(options => {
            // remove duplicit options
            const uniqueOptions = [...new Set(options)].filter(o => o.length > 0);

            // remove unnecessary options
            return [...uniqueOptions, ""];
        });
    }

    // paste multiple options separated by new line
    function handlePaste(e: React.ClipboardEvent<HTMLInputElement>, optionIndex: number) {
        e.preventDefault();

        const pastedText = e.clipboardData.getData("text/plain");
        const lines = pastedText
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(line => line !== "");

        if (lines.length > 0) {
            setOptions([
                ...options.filter((_, i) => i < optionIndex),
                options[optionIndex] + lines[0],
                ...lines.filter((_, i) => i > 0),
                ...options.filter((_, i) => i > optionIndex),
            ]);
            handleBlurOption();
        }
    }

    async function handleSave() {
        if (!poll.title) {
            return setMessage(dict.pollFormMissingTitle);
        }

        const clearOptions = options.filter(o => o.length > 0);
        if (clearOptions.length < 2) {
            return setMessage(dict.pollFormMissingOptions);
        }
        const pollOptions: Record<string, PollOption> = {};
        clearOptions.forEach(option => pollOptions[option] = { description: "", users: {} });

        setIsSending(true);
        setMessage("");

        const [id, error] = await api.addPoll({
            ...poll,
            options: pollOptions,
            userId: getLocalId(),
            lang,
        });

        setIsSending(false);

        if (!id) {
            handleError(error);
            setMessage(dict.pollFormError);
        } else {
            setMessage(dict.pollFormSuccess);
            push(`/${dict.urlPoll}/${id}`);
        }
    }

    return <Page fixed={<>
        <Header title={dict.what} link={`/${dict.urlPoll}`}>
            <LangSwitch />
            <ThemeSwitch />
        </Header>
        <Footer />
    </>}>

        <h1>{dict.newPoll}</h1>

        <div className="form">
            <label>{dict.theme}</label>
            <ThemeSelect
                value={poll.theme}
                onChange={theme => setPoll({ ...poll, theme })}
            />

            <Input autoFocus label={dict.pollFormTitle} field="title" maxLength={100} required value={poll} setValue={setPoll} />
            <Input label={dict.pollFormDescription} field="description" maxLength={500} type="textarea" value={poll} setValue={setPoll} />

            <div>
                <label>{dict.pollFormVoteCount}</label>
                <InputNumber value={poll.voteCount} min={1} onChange={voteCount => setPoll({ ...poll, voteCount })} />
            </div>

            <label>
                {dict.pollFormOptions}
                <p className="caption">{dict.pollFormOptionsCaption}</p>
            </label>
            {options.map((option, i) => <input
                key={i}
                style={{ marginBottom: "8px" }}
                type="text"
                placeholder={dict.pollFormOptionPlaceholder}
                value={option}
                onChange={e => handleChangeOption(i, e.target.value)}
                onBlur={handleBlurOption}
                onPaste={e => handlePaste(e, i)}
            />)}

            <CheckboxPublic
                value={poll}
                onChange={setPoll}
                label={dict.pollFormPublic}
                captionPublic={dict.pollFormPublicCaption}
                captionPrivate={dict.pollFormPrivateCaption}
            />

            {message && <p className="alert">{message}</p>}

            {isSending && <Loading />}
            {!isSending && <button onClick={handleSave}>{dict.pollCreate}</button>}
        </div>

    </Page>;
}